import type { Dispatch, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LoadMoreSentinel } from '../../shared/load_more_sentinel.js';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import { ResultsFiltersSheet } from '../../shared/results_page/results_filters_sheet.js';
import {
  ResultsLayout,
  type ResultsLayoutProps,
} from '../../shared/results_page/results_layout.js';
import { atLeast } from '@voll-intelligence/ui-kit/breakpoints';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { shareAction, type ExplorerShare } from './explorer_share.js';

export type { ExplorerShare } from './explorer_share.js';
import type { FlightMoneySource } from '../results/flight_offer_source.js';
import type { FlightExplorerCopy } from './explorer_copy.js';
import { useExplorerEnv, type FlightExplorerEnv } from './explorer_env.js';
import {
  ExplorerFilterChips,
  MetaActions,
  type ExplorerPinnedFilter,
} from './explorer_controls.js';
import { ExplorerOfflineFoot } from './explorer_offline_foot.js';
import { fmtWeekdayDayMonth, fmtMoneyWhole } from '../results/flight_format.js';
import { sumMoney } from '../results/sum_money.js';
import { isResolved, LegStepper, type LegStep } from '../results/leg_stepper.js';
import type { CityOfAirport } from '../results/filter_logic.js';
import {
  activeAxisCount,
  airportSidesOf,
  defaultFilterState,
  mirrorAirportSide,
  priceBounds,
  type FlightFilterState,
} from '../results/filter_logic.js';
import {
  FlightFilterChips,
  type FlightFilterChipNames as ChipNames,
} from '../results/filters/flight_filter_chips.js';
import {
  FlightFilterPanel,
  type FlightFilterCabin,
} from '../results/filters/flight_filter_panel.js';
import { searchingLeg, type ExplorerEvent, type ExplorerState } from './explorer_state.js';
import type { FilterIntentControl } from './use_filter_intent.js';
import { useCabinRequest } from './use_cabin_request.js';
import { useListScrollReset } from './use_list_scroll_reset.js';
import { useRefineFocus } from './use_refine_focus.js';
import type { FlightExplorerOffer, FlightSearchFacets } from './search_source.js';
import { OutboundGoneHint } from './explorer_outbound_gone.js';
import {
  AirportSwitchHint,
  ExplorerHints,
  rollbackHintOf,
  type ExplorerHintsProps,
} from './explorer_hints.js';

/**
 * A coluna de resultados do explorer sobre o layout compartilhado de
 * resultados (`ResultsLayout`, #2994): título h1 (+ compartilhar), a fila de
 * trechos quando há trecho já escolhido ou aberto além do primeiro, a meta
 * (placar do roteiro ou contagem) com o ordenador, os cortes na coluna do
 * layout (aberta por padrão; fora da linha viram a folha com rascunho e
 * Aplicar) e a lista. Sem aside: a linha centra em 1080px.
 *
 * A coluna de cortes existe SEMPRE, como no hotel e no carro: o que muda por
 * dentro é do `FlightFilterPanel` (esqueleto na espera, cabine com a lista
 * vazia, eixos quando há o que cortar). Um portão aqui em cima fazia a coluna
 * nascer e sumir com a lista, mexendo na linha a cada busca.
 *
 * O aviso de teto de política saiu (decisão de #2994); a linha "Trecho N ·
 * N melhores opções" do stepper também — a contagem mora na meta.
 */

/** Os cinco critérios do desenho (#2109), na ordem em que o menu os lista:
 *  Recomendado (default) · Preço · Duração · Partida · Chegada. */

/** O total parcial dos trechos já escolhidos — `undefined` sem escolha ou com
 *  moedas misturadas (`sumMoney`). */
function partialTotal(state: ExplorerState): FlightMoneySource | undefined {
  const picked = Object.values(state.chosen).flatMap((p) =>
    p?.summary ? [{ amount: p.summary.total, currency: p.summary.currency }] : [],
  );
  return sumMoney(picked);
}

/**
 * A contagem tem número pra mostrar?
 *
 * Cala em três casos, e cada um por um motivo próprio: `loading` (o número ainda
 * não existe — ao lado do esqueleto ele lê como resultado vazio), `idle` NÃO
 * congelado (mesma coisa, é a busca de montagem) e `error` (aí "0 voos" fica ao
 * lado de um alerta, inventando um resultado que a busca não deu). `idle`
 * CONGELADO é terminal — o vazio congelado é a resposta, e ali a contagem vale.
 */
function hasCount(state: ExplorerState, frozen: boolean): boolean {
  const phase = state.legs[state.activeLeg]?.phase;
  if (phase === 'error') return false;
  if (phase === 'loading') return false;
  return phase !== 'idle' || frozen;
}

/**
 * O TÍTULO da lista — o trecho ATIVO responde. No round trip ele nomeia o
 * SENTIDO da fase ("Escolha a ida — REC · Recife · Seg, 10 ago"): na fase 2 o trecho é a
 * VOLTA, cujo destino é a ORIGEM da viagem e cuja data é `returnDate`. Fora do
 * round trip, `leg` é `null` e o host cai na cópia sem sentido ("Voos para …").
 * Cidade pela autoridade do domain (`cityByAirport`); IATA fora do cadastro
 * repete no lugar da cidade. `null` sem data real: título com buraco mentiria.
 */
interface ExplorerListTitle {
  iata: string;
  city: string;
  date: string;
  leg: 'outbound' | 'return' | null;
}

export function listTitleOf(
  state: ExplorerState,
  locale: string,
  cityOf: CityOfAirport,
): ExplorerListTitle | null {
  const first = state.request.legs[0];
  if (!first) return null;
  const roundTrip = state.request.mode === 'round_trip';
  const returnPhase = roundTrip && state.activeLeg === 1;
  const active = state.request.legs[state.activeLeg] ?? first;
  const iata = returnPhase ? first.origin : active.destination;
  const date = fmtWeekdayDayMonth(
    returnPhase ? (state.request.returnDate ?? '') : active.departDate,
    locale,
  );
  if (!date) return null;
  let leg: 'outbound' | 'return' | null = null;
  if (roundTrip) leg = returnPhase ? 'return' : 'outbound';
  return { iata, city: cityOf(iata) ?? iata, date, leg };
}

function headingOf(title: ExplorerListTitle, copy: FlightExplorerCopy): string {
  const { iata, city, date, leg } = title;
  if (!leg) return copy.page.listTitle(city, date);
  const titleOf = leg === 'return' ? copy.page.listTitleReturn : copy.page.listTitleOutbound;
  return titleOf(iata, city, date);
}

/** O placar do roteiro na meta: [contagem ·] N de M escolhidos + o total
 *  (parcial, ou fechado no último). `isResolved` é o MESMO predicado do stepper.
 *  Dois blocos que quebram ENTRE si (a meta do layout quebra no celular): o total
 *  vai inteiro para a linha de baixo em vez de ser cortado (#3844). */
function scoreOf(
  count: string | null,
  steps: LegStep[],
  state: ExplorerState,
  copy: FlightExplorerCopy,
): ReactNode {
  const chosen = steps.filter(isResolved).length;
  const complete = steps.length > 0 && chosen === steps.length;
  const partial = partialTotal(state);
  const score = copy.stepper.chosenCount(chosen, steps.length);
  return (
    <span className="flex flex-wrap gap-x-2.5">
      <span>
        {count && <span>{count}</span>}
        {count && ' · '}
        <span>{score}</span>
      </span>
      {partial && (
        <span className="font-mono whitespace-nowrap tabular-nums">
          {complete ? copy.stepper.total : copy.stepper.partialTotal} ·{' '}
          <b className="text-foreground">{fmtMoneyWhole(partial)}</b>
        </span>
      )}
    </span>
  );
}

function countOf(rows: number, total: number | undefined, copy: FlightExplorerCopy): string {
  if (total !== undefined && total > rows) {
    return copy.page.foundOfTotal(rows, total);
  }
  return copy.page.found(rows);
}

/** A fila de trechos só entra a partir da VOLTA (ou da 2ª seleção do
 *  multitrecho) — na primeira escolha a tela é a de só ida (#2994). Um trecho
 *  já resolvido a mantém em cena mesmo com o primeiro reaberto: é por ela que
 *  se volta ao trecho escolhido. */
function showSteps(steps: LegStep[] | null, state: ExplorerState): steps is LegStep[] {
  return steps !== null && (state.activeLeg > 0 || steps.some(isResolved));
}

/** O seam de paginação do explorer (`loadMoreSeam`): a sentinela e o teto do conjunto. */
type ExplorerPageSeam = {
  readonly loadMore?: (() => void) | undefined;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly total?: number | undefined;
};

export type ExplorerResultsPageProps = {
  readonly state: ExplorerState;
  readonly steps: LegStep[] | null;
  readonly rows: FlightExplorerOffer[];
  readonly offers: FlightExplorerOffer[];
  readonly frozen: boolean;
  readonly refining: boolean;
  readonly band: ReactNode;
  readonly hints: ExplorerHintsProps;
  readonly send: Dispatch<ExplorerEvent>;
  readonly page: ExplorerPageSeam;
  readonly pinnedFilter?: ExplorerPinnedFilter | undefined;
  readonly facets: FlightSearchFacets | undefined;
  readonly cabin: FlightFilterCabin | undefined;
  /** O composer "Filtre do seu jeito" + os chips, no pé da coluna de cortes.
   *  Ausente (sem seam `interpretFilter`, ou explorer congelado) ⇒ nada é desenhado. */
  readonly textFilter?: FilterIntentControl | undefined;
  readonly share?: ExplorerShare | undefined;
  readonly children: ReactNode;
};

function stepsBanner(
  steps: LegStep[] | null,
  state: ExplorerState,
  frozen: boolean,
  send: Dispatch<ExplorerEvent>,
  env: FlightExplorerEnv,
): ReactNode {
  if (!showSteps(steps, state)) return undefined;
  return (
    <LegStepper
      text={{ copy: env.copy.stepper, locale: env.locale, cityOf: env.places.cityOf }}
      steps={steps}
      active={state.activeLeg}
      headless
      onGoTo={(i) => {
        if (!frozen) send({ t: 'go_to_leg', index: i });
      }}
      onRemove={(i) => {
        if (!frozen) send({ t: 'pick_removed', leg: i });
      }}
      frozen={frozen}
      {...(state.request.mode === 'round_trip' ? { waitingFor: 'outbound' as const } : {})}
    />
  );
}

/**
 * Os NOMES que o rótulo do chip precisa e que só a tela tem: a companhia vem das
 * facetas do conjunto (o fio manda `G3`, a pessoa lê "GOL"), o nome do aeroporto vem
 * da autoridade do domain (popular > cidade > IATA cru), a PONTA vem das ofertas em
 * cena (o fio só filtra por `airports`, não sabe se é chegada ou saída — #3495), e a
 * moeda é a das ofertas em cena — sem ela o chip de preço mostra o número cru. Código
 * fora do cadastro repete no lugar do nome. `legLabel` (R2-D3) nomeia a perna do chip
 * pelo `state` — perna ativa vira `undefined` (sem prefixo).
 *
 * `airportSide` só enxerga as ofertas da perna ATIVA (`offers`) — um chip de OUTRA perna
 * (deferred, R2-D3) não pode ler a ponta dela ali. Numa ida-e-volta a volta é o ESPELHO da
 * ida (`mirrorAirportSide`, #3495 rodada 2); um roteiro multi não tem essa relação entre
 * pernas não-adjacentes, então a perna não-ativa cai em `unknown` (a regra do rótulo cai no
 * padrão "Saindo de" sem side conhecido) em vez de adivinhar com as ofertas erradas.
 */
function chipNamesOf(
  facets: FlightSearchFacets | undefined,
  currency: string | undefined,
  offers: FlightExplorerOffer[],
  state: ExplorerState,
  env: FlightExplorerEnv,
): ChipNames {
  const { copy, places } = env;
  const activeSide = airportSidesOf(offers);
  return {
    airline: (code) => facets?.airlines.find((airline) => airline.code === code)?.name ?? code,
    airport: (iata) => places.airportName(iata),
    airportSide: (iata, leg) => {
      if (leg === undefined || leg === state.activeLeg) return activeSide(iata);
      if (state.request.mode === 'round_trip') return mirrorAirportSide(activeSide(iata));
      return 'unknown';
    },
    ...(currency ? { currency } : {}),
    legLabel: (leg) => {
      if (leg === state.activeLeg) return undefined;
      if (state.request.mode === 'round_trip')
        return leg === 0 ? copy.page.chipLeg.outbound : copy.page.chipLeg.inbound;
      return copy.page.chipLeg.n(leg + 1);
    },
  };
}

/** A coluna de cortes e a folha dela (fora da linha): rascunho + Aplicar, sobre
 *  o MESMO painel do voo — na coluna sem o cabeçalho próprio (o layout o tem).
 *  O composer do filtro por texto é o RODAPÉ fixo dos dois: `filtersFooter` da
 *  coluna e `footer` da folha, acima do Aplicar. Dois verbos no mesmo pé, de
 *  propósito — o composer aplica na hora (cria o chip), o Aplicar comete o
 *  rascunho dos eixos. */
function filtersOf(input: {
  readonly state: ExplorerState;
  readonly offers: FlightExplorerOffer[];
  readonly facets: FlightSearchFacets | undefined;
  readonly cabin: FlightFilterCabin | undefined;
  readonly frozen: boolean;
  readonly refining: boolean;
  readonly textFilter: FilterIntentControl | undefined;
  readonly currency: string | undefined;
  readonly applyFilter: (filter: FlightFilterState) => void;
  readonly env: FlightExplorerEnv;
}): Pick<ResultsLayoutProps, 'filters' | 'filtersFooter' | 'filtersSheet'> {
  const { state, offers, facets, cabin, frozen, textFilter, currency, applyFilter, env } = input;
  const { copy } = env;
  // Refine em voo SEM oferta em mão é espera como a da busca: sem os eixos em
  // esqueleto o painel ficava em branco até a página chegar (#3844).
  const loading = (searchingLeg(state) || (input.refining && offers.length === 0)) && !frozen;
  const panel = (
    value: FlightFilterState,
    onChange: (next: FlightFilterState) => void,
    column: boolean,
    onClose?: () => void,
  ) => (
    <FlightFilterPanel
      copy={copy.filter}
      locale={env.locale}
      cityOf={env.places.cityOf}
      onFacetApplied={(facet) => {
        env.track('filter_applied', { facet });
      }}
      offers={offers}
      state={value}
      facets={facets}
      loading={loading}
      variant={column ? 'column' : 'drawer'}
      header={!column}
      {...(onClose ? { onClose } : {})}
      {...(cabin ? { cabin } : {})}
      onChange={onChange}
    />
  );
  const composer = textFilter && (
    <FlightFilterChips
      copy={copy.chips}
      dictation={env.dictation}
      chips={textFilter.chips}
      note={textFilter.note}
      noteValues={textFilter.noteValues}
      busy={textFilter.busy}
      disabled={frozen}
      names={chipNamesOf(facets, currency, offers, state, env)}
      onSubmit={textFilter.submit}
      onRemove={textFilter.remove}
    />
  );
  return {
    filters: panel(state.filter, applyFilter, true),
    filtersFooter: composer,
    filtersSheet: ({ open, onOpenChange, returnFocusTo }) => (
      <ResultsFiltersSheet
        open={open}
        onOpenChange={onOpenChange}
        returnFocusTo={returnFocusTo}
        value={state.filter}
        label={copy.filter.filters}
        applyLabel={copy.page.apply}
        locked={frozen}
        onApply={applyFilter}
        footer={composer}
      >
        {({ draft, setDraft, close }) => (
          // A folha só dá altura e rolagem ao painel de CASCA (`.iuk-results-filters`,
          // hotel e carro); o do voo vai cru e, sem esta casca, crescia além da folha,
          // era clipado pelo Drawer e empurrava o Aplicar para fora da tela (#3299).
          <div className="scrollbar-rail [scrollbar-gutter:auto]! min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {panel(draft, setDraft, false, close)}
          </div>
        )}
      </ResultsFiltersSheet>
    ),
  };
}

/** A meta: a contagem do trecho aberto e, no roteiro, o placar "N de M
 *  escolhidos" (+ total) ao lado — ver `scoreOf`. */
function metaOf(input: {
  readonly steps: LegStep[] | null;
  readonly state: ExplorerState;
  readonly rows: FlightExplorerOffer[];
  readonly total: number | undefined;
  readonly frozen: boolean;
  readonly copy: FlightExplorerCopy;
}): ReactNode {
  const { steps, state, rows, total, frozen, copy } = input;
  const count = hasCount(state, frozen) ? countOf(rows.length, total, copy) : null;
  if (!steps) return count;
  return scoreOf(count, steps, state, copy);
}

export function ExplorerResultsPage({
  state,
  steps,
  rows,
  offers,
  frozen,
  refining,
  band,
  hints,
  send,
  page,
  pinnedFilter,
  facets,
  cabin,
  textFilter,
  share,
  children,
}: ExplorerResultsPageProps): ReactNode {
  const env = useExplorerEnv();
  const { copy, locale, toolbarSlot: resultsToolbarSlot } = env;
  useCabinRequest({ state, send, cabin, notify: textFilter?.notify });
  const scrollerRef = useListScrollReset(state);
  const listRef = useRefineFocus(refining);
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  // A partir de `wide` a casca trava a altura do `.fex-results` (que rola o
  // estágio de tarifas): a lista rola no próprio `<main>`, com a faixa e a fila de
  // trechos fixas acima e a barra ao lado da lista (#4587). Abaixo dela a casca
  // empilha e quem rola é o host.
  const wide = useMediaQuery(atLeast('wide'));
  // Os chips só existem no estreito e com o slot do host aberto: a segunda
  // linha da barra do shell ou a do takeover (`ExplorerTopBar`), como no hotel.
  const chipsSlot = narrow && resultsToolbarSlot ? resultsToolbarSlot : null;
  const title = listTitleOf(state, locale, env.places.cityOf);
  const ceiling = facets?.priceRange?.max ?? priceBounds(offers)[1];
  const applyFilter = (filter: FlightFilterState) => {
    if (!frozen) send({ t: 'filter_changed', filter });
  };
  /** Redefinir zera os chips JUNTO com os eixos: um chip descreve um corte que, depois
   *  do reset, não existe mais. `filter_changed` não serve — ele só derruba os chips que
   *  TOCAM o campo mexido, e aqui a mexida é em todos de uma vez. */
  const resetFilters = () => {
    if (!frozen) send({ t: 'filter_reset', filter: defaultFilterState(offers, ceiling) });
  };

  const activeCount = activeAxisCount(offers, state.filter, ceiling);
  const filterSlots = filtersOf({
    state,
    offers,
    facets,
    cabin,
    frozen,
    refining,
    textFilter,
    currency: offers[0]?.price.currency,
    applyFilter,
    env,
  });

  return (
    <div
      ref={scrollerRef}
      className={refining ? 'fex-results is-refining' : 'fex-results'}
      aria-busy={refining}
    >
      {chipsSlot &&
        filterSlots.filtersSheet &&
        createPortal(
          <ExplorerFilterChips
            filter={state.filter}
            offers={offers}
            activeCount={activeCount}
            frozen={frozen}
            pinned={pinnedFilter}
            filtersSheet={filterSlots.filtersSheet}
            onFilterChange={applyFilter}
            onSortChange={(sort) => {
              send({ t: 'sort_changed', sort });
            }}
          />,
          chipsSlot,
        )}
      {band}
      <ResultsLayout
        copy={{
          filtersLabel: copy.filter.filters,
          resetFiltersLabel: copy.filter.reset,
        }}
        narrow={narrow}
        listScroll={wide ? 'own' : 'host'}
        title={title ? headingOf(title, copy) : copy.page.title}
        titleActions={shareAction(share, rows, copy.page)}
        banner={stepsBanner(steps, state, frozen, send, env)}
        meta={metaOf({ steps, state, rows, total: page.total, frozen, copy })}
        metaActions={
          <MetaActions
            state={state}
            offers={offers}
            frozen={frozen}
            pinnedFilter={pinnedFilter}
            send={send}
          />
        }
        activeFilterCount={activeCount}
        onResetFilters={resetFilters}
        metaControls={chipsSlot ? 'none' : 'inline'}
        {...filterSlots}
        content={
          <>
            <ExplorerHints {...hints} rollback={rollbackHintOf(state, cabin)} />
            <AirportSwitchHint state={state} rows={rows} />
            <OutboundGoneHint state={state} />
            <div ref={listRef} className="fex-list" inert={refining}>
              {children}
              <LoadMoreSentinel
                hasMore={page.hasMore}
                loading={page.loadingMore}
                label={copy.page.loadMore}
                onLoadMore={page.loadMore}
              />
              <ExplorerOfflineFoot state={state} frozen={frozen} />
            </div>
          </>
        }
      />
    </div>
  );
}
